import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { TextPromptDialog } from '@/components/ui/TextPromptDialog';
import { toast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/cn';
import { useDoctors, useEncounterMutation, useSpecialties } from '../hooks';
import { createConsult, createProcedure, updateConsult } from '../api';
import { Section, errorInfo, minutesLabel, timeLabel } from '../ui';
import type { Consult, Procedure, Workspace } from '../types';

import VoiceFormFiller, { type VoiceField } from '@/components/VoiceFormFiller';
import { appendText, matchByName, selectField, textField, vOneOf, vText, type Extracted } from '@/lib/voiceFill';

const OUTCOMES = ['successful', 'partial', 'unsuccessful', 'abandoned'] as const;
const URGENCIES = ['emergency', 'urgent', 'routine'] as const;

const COMMON_PROCEDURES = ['CPR', 'Endotracheal intubation', 'Defibrillation / cardioversion', 'Central line', 'Chest tube',
  'Suturing', 'Wound dressing', 'Splinting / POP', 'Gastric lavage', 'Nebulisation', 'Catheterisation', 'Lumbar puncture'];

/**
 * Procedures are the CLINICAL record (who did what, when, outcome) — the
 * money for them is a separate charge. Consults carry a response deadline
 * computed server-side from the ED settings; overdue ones are flagged on the
 * board and can be escalated, and a specialist who accepts ownership becomes
 * the attending (D10).
 */
export default function ProceduresConsultsSection({ ws }: { ws: Workspace }) {
  const { user } = useAuth();
  const id = ws.encounter.id;
  const canWrite = !!user?.modules?.includes('er:clinical');
  const open = ws.encounter.status !== 'departed';
  const specialties = useSpecialties();
  const doctors = useDoctors();
  const [p, setP] = useState<Partial<Procedure>>({});
  const [c, setC] = useState<{ specialty_id?: number; consultant_id?: number; urgency: Consult['urgency']; reason: string }>({ urgency: 'urgent', reason: '' });
  // Escalate / cancel ask in a dialog — window.prompt() is refused by embedded browsers.
  const [consultAsk, setConsultAsk] = useState<{ consult: Consult; kind: 'escalate' | 'cancel' } | null>(null);
  const [completeFor, setCompleteFor] = useState<{ consult: Consult; advice: string; owner: boolean } | null>(null);

  const addProc = useEncounterMutation(id, (input: Partial<Procedure>) => createProcedure(id, input));
  const addConsult = useEncounterMutation(id, (input: Parameters<typeof createConsult>[1]) => createConsult(id, input));
  const upd = useEncounterMutation(id, (v: { cid: number; body: Parameters<typeof updateConsult>[1] }) => updateConsult(v.cid, v.body));

  const procVoice: VoiceField[] = [
    textField('procedure_name', `Procedure performed, e.g. ${COMMON_PROCEDURES.slice(0, 4).join(', ')}`),
    textField('assisted_by', 'Assisted by (names)'),
    selectField('outcome', 'Outcome — successful, partial, unsuccessful, abandoned', OUTCOMES),
    textField('notes', 'Procedure notes — findings, complications'),
  ];
  const applyProc = (x: Extracted) => setP((cur) => {
    const next = { ...cur };
    const name = vText(x, 'procedure_name'); if (name) next.procedure_name = name;
    const by = vText(x, 'assisted_by'); if (by) next.assisted_by = by;
    const out = vOneOf(x, 'outcome', OUTCOMES); if (out) next.outcome = out;
    const notes = vText(x, 'notes'); if (notes) next.notes = appendText(cur.notes, notes, '\n');
    return next;
  });
  const consultVoice: VoiceField[] = [
    textField('specialty', 'Specialty to consult, e.g. Orthopaedics, Neurosurgery'),
    selectField('urgency', 'Urgency — emergency, urgent, routine', URGENCIES),
    textField('consultant', 'Named consultant, only if a specific doctor was asked for'),
    textField('reason', 'Reason for the consult / clinical question'),
  ];
  const applyConsult = (x: Extracted) => setC((cur) => {
    const next = { ...cur };
    const sp = matchByName(vText(x, 'specialty'), specialties.data ?? [], (s) => s.name); if (sp) next.specialty_id = sp.id;
    const urg = vOneOf(x, 'urgency', URGENCIES); if (urg) next.urgency = urg;
    const doc = matchByName(vText(x, 'consultant'), doctors.data ?? [], (d) => d.full_name); if (doc) next.consultant_id = doc.id;
    const reason = vText(x, 'reason'); if (reason) next.reason = appendText(cur.reason, reason, '; ');
    return next;
  });

  const run = (pr: Promise<unknown>, ok?: string) => pr.then(() => ok && toast({ title: ok, variant: 'success' })).catch((e) => toast({ title: errorInfo(e).message, variant: 'danger' }));

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Section title="Procedures">
        {canWrite && open && (
          <div className="mb-3 grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <VoiceFormFiller fields={procVoice} onFields={applyProc} label="🎙 Dictate procedure"
                hint="Endotracheal intubation, assisted by Nurse Priya, successful at first attempt, size 8 tube" />
            </div>
            <FormField label="Procedure" required className="col-span-2">
              <Input list="er-proc-list" value={p.procedure_name ?? ''} onChange={(e) => setP({ ...p, procedure_name: e.target.value })} />
              <datalist id="er-proc-list">{COMMON_PROCEDURES.map((x) => <option key={x} value={x} />)}</datalist>
            </FormField>
            <FormField label="Assisted by"><Input value={p.assisted_by ?? ''} onChange={(e) => setP({ ...p, assisted_by: e.target.value })} /></FormField>
            <FormField label="Outcome">
              <NativeSelect value={p.outcome ?? ''} onChange={(e) => setP({ ...p, outcome: e.target.value || null })}>
                <option value="">—</option><option value="successful">Successful</option><option value="partial">Partial</option>
                <option value="unsuccessful">Unsuccessful</option><option value="abandoned">Abandoned</option>
              </NativeSelect>
            </FormField>
            <FormField label="Notes" className="col-span-2"><Textarea rows={2} value={p.notes ?? ''} onChange={(e) => setP({ ...p, notes: e.target.value })} /></FormField>
            <div className="col-span-2 flex justify-end">
              <Button size="sm" loading={addProc.isPending} disabled={!p.procedure_name?.trim()} onClick={() => run(addProc.mutateAsync(p).then(() => setP({})), 'Procedure recorded')}>Record procedure</Button>
            </div>
          </div>
        )}
        {ws.procedures.length === 0 ? <div className="text-[13px] text-ink-muted">None recorded.</div> : (
          <ul className="space-y-1.5 text-[12px]">
            {ws.procedures.map((x) => (
              <li key={x.id} className="rounded-lg border border-border p-2">
                <b>{x.procedure_name}</b>{x.outcome && <Badge variant="neutral" className="ml-2">{x.outcome}</Badge>}
                <div className="text-ink-muted">{timeLabel(x.started_at)} · {x.performed_by}{x.assisted_by ? `, assisted by ${x.assisted_by}` : ''}</div>
                {x.notes && <div>{x.notes}</div>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Specialist consults">
        {canWrite && open && (
          <div className="mb-3 grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <VoiceFormFiller fields={consultVoice} onFields={applyConsult} label="🎙 Dictate consult request"
                hint="Urgent neurosurgery consult for head injury with GCS 12 and CT showing subdural bleed" />
            </div>
            <FormField label="Specialty" required>
              <NativeSelect value={c.specialty_id ?? ''} onChange={(e) => setC({ ...c, specialty_id: e.target.value ? Number(e.target.value) : undefined })}>
                <option value="">—</option>
                {(specialties.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Urgency">
              <NativeSelect value={c.urgency} onChange={(e) => setC({ ...c, urgency: e.target.value as Consult['urgency'] })}>
                <option value="emergency">Emergency</option><option value="urgent">Urgent</option><option value="routine">Routine</option>
              </NativeSelect>
            </FormField>
            <FormField label="Consultant (optional)" className="col-span-2">
              <NativeSelect value={c.consultant_id ?? ''} onChange={(e) => setC({ ...c, consultant_id: e.target.value ? Number(e.target.value) : undefined })}>
                <option value="">On-call</option>
                {(doctors.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.full_name}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Reason" required className="col-span-2"><Input value={c.reason} onChange={(e) => setC({ ...c, reason: e.target.value })} /></FormField>
            <div className="col-span-2 flex justify-end">
              <Button size="sm" loading={addConsult.isPending} disabled={!c.specialty_id || !c.reason.trim()}
                onClick={() => run(addConsult.mutateAsync(c).then(() => setC({ urgency: 'urgent', reason: '' })), 'Consult requested')}>Request consult</Button>
            </div>
          </div>
        )}
        {ws.consults.length === 0 ? <div className="text-[13px] text-ink-muted">No consults.</div> : (
          <div className="space-y-2">
            {ws.consults.map((x) => {
              const overdue = x.status === 'requested' && new Date(x.response_due_at) < new Date();
              const dueIn = Math.round((new Date(x.response_due_at).getTime() - Date.now()) / 60000);
              return (
                <div key={x.id} className={cn('rounded-lg border p-2.5 text-[12px]', overdue ? 'border-danger bg-danger-soft/40' : 'border-border')}>
                  <div className="flex flex-wrap items-center gap-2">
                    <b>{x.specialty}</b>
                    <Badge variant={x.urgency === 'emergency' ? 'danger' : x.urgency === 'urgent' ? 'warn' : 'neutral'}>{x.urgency}</Badge>
                    <Badge variant={x.status === 'completed' ? 'success' : x.status === 'cancelled' ? 'neutral' : 'info'}>{x.status}</Badge>
                    {x.accepted_ownership && <Badge variant="success">took ownership</Badge>}
                    {x.status === 'requested' && <span className={overdue ? 'font-semibold text-danger-ink' : 'text-ink-muted'}>{overdue ? `overdue ${minutesLabel(-dueIn)}` : `due in ${minutesLabel(dueIn)}`}</span>}
                  </div>
                  <div className="text-ink-muted">{x.reason} · {x.consultant_name ?? 'on-call'} · requested {timeLabel(x.requested_at)} by {x.requested_by}</div>
                  {x.escalation_note && <div className="text-warn-ink">Escalated {timeLabel(x.escalated_at)}: {x.escalation_note}</div>}
                  {x.advice && <div className="mt-1"><b>Advice:</b> {x.advice}</div>}
                  {canWrite && !['completed', 'cancelled'].includes(x.status) && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {x.status === 'requested' && <Button size="sm" variant="ghost" onClick={() => run(upd.mutateAsync({ cid: x.id, body: { action: 'respond' } }))}>Responded</Button>}
                      {x.status !== 'attended' && <Button size="sm" variant="ghost" onClick={() => run(upd.mutateAsync({ cid: x.id, body: { action: 'arrive' } }))}>At bedside</Button>}
                      <Button size="sm" onClick={() => setCompleteFor({ consult: x, advice: x.advice ?? '', owner: false })}>Complete…</Button>
                      {['requested', 'responded'].includes(x.status) && (
                        <Button size="sm" variant="ghost" onClick={() => { upd.reset(); setConsultAsk({ consult: x, kind: 'escalate' }); }}>Escalate</Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => { upd.reset(); setConsultAsk({ consult: x, kind: 'cancel' }); }}>Cancel</Button>
                    </div>
                  )}
                  {completeFor?.consult.id === x.id && (
                    <div className="mt-2 space-y-2 rounded-lg bg-muted p-2">
                      <Textarea rows={2} placeholder="Specialist's advice (required)" value={completeFor.advice} onChange={(e) => setCompleteFor({ ...completeFor, advice: e.target.value })} />
                      <label className="flex items-center gap-2"><Checkbox checked={completeFor.owner} onCheckedChange={(v) => setCompleteFor({ ...completeFor, owner: v === true })} /> Specialist accepted ownership of the patient{x.consultant_name ? ` (${x.consultant_name} becomes attending)` : ''}</label>
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setCompleteFor(null)}>Cancel</Button>
                        <Button size="sm" disabled={!completeFor.advice.trim()} onClick={() => run(upd.mutateAsync({ cid: x.id, body: { action: 'complete', advice: completeFor.advice, accepted_ownership: completeFor.owner } }).then(() => setCompleteFor(null)), 'Consult completed')}>Save</Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>
      <TextPromptDialog
        open={!!consultAsk}
        title={consultAsk ? `${consultAsk.kind === 'escalate' ? 'Escalate' : 'Cancel'} the ${consultAsk.consult.specialty} consult?` : ''}
        description={consultAsk?.kind === 'escalate'
          ? 'The escalation is recorded on the consult with the time.'
          : 'The consult request is closed as cancelled and the specialist is no longer expected.'}
        label={consultAsk?.kind === 'escalate' ? 'Escalated to whom / how?' : 'Reason for cancelling'}
        confirmLabel={consultAsk?.kind === 'escalate' ? 'Record escalation' : 'Cancel consult'}
        danger={consultAsk?.kind === 'cancel'}
        pending={upd.isPending}
        error={upd.error ? errorInfo(upd.error).message : null}
        onConfirm={(text) => {
          if (!consultAsk) return;
          const body = consultAsk.kind === 'escalate'
            ? { action: 'escalate' as const, escalation_note: text }
            : { action: 'cancel' as const, cancelled_reason: text };
          upd.mutate({ cid: consultAsk.consult.id, body }, { onSuccess: () => setConsultAsk(null) });
        }}
        onClose={() => setConsultAsk(null)}
      />
    </div>
  );
}
